/**
 * French Beauty Center theme · global behaviour
 * Vanilla JS, no libraries. Everything degrades to plain links/forms without JS.
 */
(() => {
  const FBC = window.FBC || (window.FBC = { routes: {}, strings: {} });
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Parse an HTML string and return the first element matching the selector.
   * @param {string} html
   * @param {string} selector
   * @returns {Element|null}
   */
  const pick = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

  /** @param {string} message */
  const toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.textContent = ''; }, 300);
    }, 2600);
  };

  /** @param {string} path - path without leading slash, localised with the current root (/ or /ar/) */
  const localUrl = (path) => `${FBC.routes.root || '/'}${path}`.replace(/\/{2,}/g, '/');

  FBC.toast = toast;
  FBC.pick = pick;

  /* ------------------------------------------------------------------ */
  /* Dialogs (drawers): open, close, focus return, scroll lock          */
  /* ------------------------------------------------------------------ */
  let lastTrigger = null;

  /** @param {string} id @param {Element} [trigger] */
  const openDialog = (id, trigger) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.body.classList.add('is-locked');
    const focusTarget = dialog.querySelector('[autofocus], [data-dialog-focus]') || dialog.querySelector('button, a[href], input');
    focusTarget?.focus();
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  /** @param {HTMLDialogElement} dialog */
  const closeDialog = (dialog) => {
    if (!dialog?.open) return;
    dialog.close();
  };

  document.addEventListener('close', (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const id = opener.getAttribute('data-dialog-open');
      if (document.getElementById(id)) {
        event.preventDefault();
        openDialog(id, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop (the dialog element itself, outside its inner box)
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  FBC.openDialog = openDialog;
  FBC.closeDialog = closeDialog;

  /* ------------------------------------------------------------------ */
  /* Sticky header: shrink after 80px of scroll                          */
  /* ------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.classList.toggle('is-scrolled', window.scrollY > 80);
        this.ticking = false;
      });
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------ */
  /* Desktop dropdown navigation                                         */
  /* ------------------------------------------------------------------ */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const closeDropdowns = (except) => {
    for (const d of document.querySelectorAll('[data-nav-dropdown][open]')) {
      if (d !== except) d.open = false;
    }
  };

  document.addEventListener('toggle', (event) => {
    const d = event.target;
    if (d instanceof HTMLDetailsElement && d.matches('[data-nav-dropdown]') && d.open) closeDropdowns(d);
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-nav-dropdown]')) closeDropdowns();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-nav-dropdown][open]');
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  });

  const bindHover = () => {
    for (const d of document.querySelectorAll('[data-nav-dropdown]')) {
      if (d.dataset.hoverBound) continue;
      d.dataset.hoverBound = 'true';
      let timer;
      d.addEventListener('mouseenter', () => { if (!finePointer.matches) return; clearTimeout(timer); d.open = true; });
      d.addEventListener('mouseleave', () => { if (!finePointer.matches) return; timer = setTimeout(() => { d.open = false; }, 150); });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll                                                    */
  /* ------------------------------------------------------------------ */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 })
    : null;

  /** @param {ParentNode} [root] */
  const observeReveal = (root = document) => {
    for (const el of root.querySelectorAll('[data-reveal]:not(.is-revealed)')) {
      if (!revealObserver || window.Shopify?.designMode) { el.classList.add('is-revealed'); continue; }
      revealObserver.observe(el);
    }
  };
  FBC.observeReveal = observeReveal;

  /* ------------------------------------------------------------------ */
  /* Quantity steppers                                                   */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-step]');
    if (!button) return;
    const input = button.closest('.quantity')?.querySelector('input');
    if (!input) return;
    const step = Number(button.dataset.qtyStep);
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + step));
    if (next === Number(input.value)) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  const cartSections = () => {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    for (const el of document.querySelectorAll('[data-cart-section]')) ids.push(el.dataset.cartSection);
    return [...new Set(ids)];
  };

  /** @param {{item_count:number, sections?:Record<string,string>}} cart */
  const renderCart = (cart) => {
    const count = cart.item_count;
    const activeId = document.activeElement?.id;
    const drawerHadFocus = !!document.activeElement?.closest('#CartDrawer');
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = String(count);
      el.hidden = count === 0;
      el.classList.remove('is-popping');
      void el.offsetWidth;
      el.classList.add('is-popping');
    }
    if (cart.sections) {
      for (const [id, html] of Object.entries(cart.sections)) {
        if (!html) continue;
        if (id === 'cart-drawer') {
          const fresh = pick(html, '[data-cart-drawer-inner]');
          const current = document.querySelector('[data-cart-drawer-inner]');
          if (fresh && current) current.replaceWith(fresh);
        } else {
          const fresh = pick(html, `[data-cart-section="${id}"]`);
          const current = document.querySelector(`[data-cart-section="${id}"]`);
          if (fresh && current) current.replaceWith(fresh);
        }
      }
    }
    // keep keyboard focus where it was after the bag re-renders
    const again = activeId ? document.getElementById(activeId) : null;
    if (again) again.focus({ preventScroll: true });
    else if (drawerHadFocus) document.querySelector('#CartDrawer .drawer__close')?.focus();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  const fetchCartState = async () => {
    const sections = cartSections();
    const url = new URL(`${FBC.routes.cart}.js`, window.location.origin);
    const cart = await (await fetch(url)).json();
    if (sections.length) {
      const sUrl = new URL(window.location.href);
      sUrl.search = '';
      sUrl.searchParams.set('sections', sections.join(','));
      cart.sections = await (await fetch(sUrl)).json();
    }
    return cart;
  };

  /**
   * Add to cart via AJAX, then refresh the drawer and open it.
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement|null} button
   */
  const addToCart = async (form, button) => {
    const formData = new FormData(form);
    const sections = cartSections();
    if (sections.length) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    try {
      const response = await fetch(`${FBC.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || FBC.strings.cartError);
      const cart = await (await fetch(`${FBC.routes.cart}.js`)).json();
      cart.sections = data.sections;
      renderCart(cart);
      if (FBC.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        openDialog('CartDrawer', button);
      } else {
        window.location.href = FBC.routes.cart;
      }
    } catch (error) {
      if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; } else { toast(error.message); }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form || FBC.cartType !== 'drawer') return;
    event.preventDefault();
    addToCart(form, form.querySelector('[type="submit"]'));
  });

  /**
   * Change a cart line quantity (0 removes).
   * @param {number} line - 1-based line index
   * @param {number} quantity
   */
  const changeLine = async (line, quantity) => {
    const sections = cartSections();
    const busy = document.querySelectorAll('[data-cart-busy]');
    for (const el of busy) el.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${FBC.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || FBC.strings.cartError);
      renderCart(cart);
    } catch (error) {
      toast(error.message || FBC.strings.cartError);
      renderCart(await fetchCartState());
    } finally {
      for (const el of document.querySelectorAll('[data-cart-busy]')) el.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-line-qty]');
    if (!input) return;
    changeLine(Number(input.dataset.cartLineQty), Number(input.value));
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(Number(remove.dataset.cartRemove), 0);
  });

  // Back/forward cache: cart may have changed in another tab
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted) return;
    renderCart(await fetchCartState());
  });

  FBC.renderCart = renderCart;

  /* ------------------------------------------------------------------ */
  /* Wishlist (saved in this browser, no app)                            */
  /* ------------------------------------------------------------------ */
  const WISHLIST_KEY = 'fbc:wishlist';

  /** @returns {string[]} */
  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  /** @param {string[]} handles */
  const writeWishlist = (handles) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify([...new Set(handles)].slice(0, 100)));
    } catch {
      /* storage full or blocked: ignore */
    }
    syncWishlistUI();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: readWishlist() } }));
  };

  const syncWishlistUI = () => {
    const handles = readWishlist();
    for (const el of document.querySelectorAll('[data-wishlist-count]')) {
      el.textContent = String(handles.length);
      el.hidden = handles.length === 0;
    }
    for (const button of document.querySelectorAll('[data-wishlist-toggle]')) {
      const saved = handles.includes(button.dataset.wishlistToggle);
      button.setAttribute('aria-pressed', String(saved));
      const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
      const text = button.querySelector('[data-wishlist-text]');
      if (text) text.textContent = saved ? FBC.strings.wishlistRemove : FBC.strings.wishlistAdd;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.wishlistToggle;
    const handles = readWishlist();
    const saved = handles.includes(handle);
    writeWishlist(saved ? handles.filter((h) => h !== handle) : [handle, ...handles]);
    toast(saved ? FBC.strings.wishlistRemoved : FBC.strings.wishlistAdded);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) syncWishlistUI();
  });

  FBC.wishlist = { read: readWishlist, write: writeWishlist, sync: syncWishlistUI, localUrl };

  /* ------------------------------------------------------------------ */
  /* Init + theme editor hooks                                           */
  /* ------------------------------------------------------------------ */
  const init = (root = document) => {
    observeReveal(root);
    bindHover();
    syncWishlistUI();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const dialog = event.target.querySelector('dialog');
    if (dialog && !dialog.open) dialog.show();
  });

  reducedMotion.addEventListener?.('change', () => document.documentElement.classList.toggle('reduced-motion', reducedMotion.matches));
  document.documentElement.classList.toggle('reduced-motion', reducedMotion.matches);
})();
